'use client';

import { useMemo } from 'react';
import { usePathname } from 'next/navigation';
import type { BottomBarItem } from './types';

/**
 * Интерфейс для элемента навигации из конфигурации проекта
 */
export interface NavigationItem {
  name: string;
  url: string;
  icon?: string;
  iconType?: 'lucide' | 'image';
  badgeKey?: string;
}

/**
 * Параметры для хука useBottomBarItems
 */
export interface UseBottomBarItemsOptions {
  /** Массив элементов навигации из конфигурации */
  navigationItems: NavigationItem[];
  /** Функция для получения переводов */
  getTranslation?: (key: string, fallback?: string) => string;
  /** Функция для получения значения бейджа по ключу */
  getBadgeValue?: (key: string) => number | null | undefined;
}

/**
 * Хук для преобразования элементов навигации из конфигурации в формат BottomBar
 */
export function useBottomBarItems({
  navigationItems,
  getTranslation,
  getBadgeValue,
}: UseBottomBarItemsOptions): BottomBarItem[] {
  const pathname = usePathname();

  return useMemo(() => {
    return navigationItems.map((navItem) => {
      // Получаем перевод метки
      const label = getTranslation
        ? getTranslation(navItem.name, navItem.name)
        : navItem.name;

      // Получаем значение бейджа, если есть badgeKey
      let badge: number | null | undefined = undefined;
      if (getBadgeValue && navItem.badgeKey) {
        badge = getBadgeValue(navItem.badgeKey);
      }

      return {
        label,
        href: navItem.url,
        icon: navItem.icon,
        iconType: navItem.iconType,
        badgeKey: navItem.badgeKey,
        badge,
      };
    });
  }, [navigationItems, getTranslation, getBadgeValue, pathname]);
}

